import { Component, Input, OnDestroy } from '@angular/core';
import { ConfigurationService, HttpService, ResponseMessage, ServiceResponse } from '@sdbn/appclient';
import { DialogService, TranslationService } from '@sdbn/common';
import { Subscription } from 'rxjs';
import { TmcContextService } from 'src/tmc-common/services/tmc-context.service';
import { Station } from 'src/tmc-yard/client/model/station';
import { CompletionOption, StationUsage, TransportCreationProfile } from 'src/tmc-yard/client/model/station-usage.model';
import { TransportDetailsLayoutConverter } from 'src/tmc-yard/common/components/tmp/transport-details-layout-converter.service';
import { TransportDetailLayout, TransportDetailMode } from 'src/tmc-yard/common/configuration/transport.configuration';
import { Transport, TransportType } from 'src/tmc-yard/common/model/transport.model';
import { TransportDetailsLayoutService } from 'src/tmc-yard/components/transport-details/transport-details-layout.service';
import { CRequestVehicleToStationByTransportCreationProfile, StationDetailsView } from './../../model/action-tablet.model';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-action-tablet-station-details',
  templateUrl: './action-tablet-station-details.component.html',
  styleUrls: ['./action-tablet-station-details.component.scss']
})
export class ActionTabletStationDetailsComponent implements OnDestroy {

  isLoading = false;

  tmcContextSubscription: Subscription;

  stationId: number | null = null;
  station: Station | null = null;

  stationDisabled = false;
  transportDetails: Transport;
  defaultCompletionOption: CompletionOption;
  transportCreationProfiles = new Array<TransportCreationProfile>();
  showTransportDetails = false;
  showHeader = false;

  stationDetailsViewValues = StationDetailsView;
  stationDetailsView: StationDetailsView;

  TransportDetailMode = TransportDetailMode;
  layoutDefinition: TransportDetailLayout;

  responseMessages: ResponseMessage[] = [];
  

  @Input("stationId") set setStationExternalKey(stationId: number) {
    this.stationId = stationId;
    this.updateModels();
  }

  constructor(
    public translationService: TranslationService,
    private tmcContextService: TmcContextService,
    private transportDetailsLayoutService: TransportDetailsLayoutService,
    private transportDetailsLayoutConverter: TransportDetailsLayoutConverter,
    private httpService: HttpService,
    public cps: ConfigurationService,
    private dialogService: DialogService
  ) {
    this.tmcContextSubscription = this.tmcContextService.stationUsagesUpdated$.subscribe(() => this.updateModels());

    this.layoutDefinition = transportDetailsLayoutConverter.convert(
      this.transportDetailsLayoutService.createActionTabeletTransportDetailsGridLayout()
    );

  }

  ngOnDestroy(): void {
    if (this.tmcContextSubscription) {
      this.tmcContextSubscription.unsubscribe();
    }
  }

  private updateModels() {
    if (this.stationId != null) {
      this.station = this.tmcContextService.findStationById(this.stationId);
      if (this.station != null) {
        this.stationDisabled = this.station.stationUsage.disabled || this.station.stationUsage.suspended;
        this.transportDetails = this.station.stationUsage.transports[0];
        this.defaultCompletionOption = this.station.stationUsage.defaultCompletionOption;
        this.transportCreationProfiles =
          this.station.stationUsage.transportCreationProfileGroups
            .map(a => a.transportCreationProfiles)
            .reduce((acc, bArray) => acc.concat(bArray), new Array<TransportCreationProfile>())
            .filter(r => r.actionTablet === true);

        this.stationDetailsView = this.calculateStationDetailsView(this.station.stationUsage);

      }
      this.showTransportDetails = this.transportDetails != null && (this.transportDetails.masterdata.transportType === TransportType.TRANSPORT
        || (this.station.stationUsage.ongoingMovementToStation));
      this.showHeader = this.transportDetails != null;
    } else {
      this.station = null;
      this.transportCreationProfiles = new Array<TransportCreationProfile>();
      this.showTransportDetails = false;
      this.stationDisabled = true;
      this.stationDetailsView = StationDetailsView.STATION_DISABLED;
    }
  }

  calculateStationDetailsView(stationUsage: StationUsage): StationDetailsView | null {
    if (stationUsage.disabled || stationUsage.suspended) {
      return StationDetailsView.STATION_DISABLED;
    } else if (stationUsage.ongoingMovementToStation) {
      return StationDetailsView.ONGOING_MOVEMENT_ORDER_TO_STATION;
    } else if (stationUsage.ongoingMovementFromStation) {
      return StationDetailsView.ONGOING_MOVEMENT_ORDER_FROM_STATION;
    } else if (!stationUsage.ongoingMovementFromStation && !stationUsage.ongoingMovementToStation) {
      return StationDetailsView.STATION_OCCUPIED;
    }
    return null;
  }


  public endSuspension() {
    if (this.station == null) {
      return;
    }
    this.httpService.postWithContext<ServiceResponse>(environment.actionTabletEndpoint + '/end-station-suspension', { id: this.station.id })
      .subscribe(() => {
        this.tmcContextService.forceRefreshOfStationUsages();
      });
  }

  public createPlannedTransportFromProfile(transportCreationProfile: TransportCreationProfile) {
    if (this.isLoading) {
      return;
    }
    if (this.cps.isEnabled('clientproperty.station-tablet.transport-creation-profile.confirm-on-execute', false)) {
      this.dialogService.showConfirmWithTitleAndMessage('Wechselbrücke anfordern', 'Möchten Sie eine neue Wechselbrücke für den Prozess "' + transportCreationProfile.displayName + '" anfordern?')
        .afterClosed().subscribe(result => {
          if (result === true) {
            this.executeTransportCreationProfile(transportCreationProfile);
          }
        });
    } else {
      this.executeTransportCreationProfile(transportCreationProfile);
    }

  }

  private executeTransportCreationProfile(transportCreationProfile: TransportCreationProfile) {
    const dto = new CRequestVehicleToStationByTransportCreationProfile();
    dto.transportCreationProfileId = transportCreationProfile.id;
    dto.targetStationId = this.stationId!;
    this.isLoading = true;
    this.httpService.postWithContext<ServiceResponse>(environment.actionTabletEndpoint + '/request-vehicle-by-transport-creation-profile', dto).subscribe(response => {
      this.responseMessages = response.responseMessages;
      this.tmcContextService.forceRefreshOfStationUsages();
      this.isLoading = false;
    }, error => {
      this.isLoading = false;
    });
  }
}
